import type {
  ChatThread,
  Colleague,
  NotificationKind,
  NotificationSeverity,
} from "../api/types.js";
import { utcMoment } from "./format.js";

/**
 * The vocabulary of chat and the notification hub.
 *
 * Same rule as everywhere else in this portal: the class carries the colour,
 * the text carries the meaning, and neither is allowed to be the only channel.
 * These boards are printed for site meetings and read by people who cannot tell
 * the amber row from the red one.
 */

// ---------------------------------------------------------- notifications

/**
 * What each severity is for, as a word.
 *
 * "Breach" is not a louder "urgent". It is a different state — a compliance
 * window closed unsatisfied, where the remedy is a recorded non-conformity
 * rather than a reminder — and the escalation ladder in the API treats it that
 * way, so the wording does too.
 */
export const SEVERITY_WORD: Record<NotificationSeverity, string> = {
  info: "For information",
  warning: "Needs planning",
  urgent: "Urgent",
  breach: "BREACHED",
};

/** The shape, so the severity survives a black-and-white printer. */
export const SEVERITY_MARK: Record<NotificationSeverity, string> = {
  info: "·",
  warning: "◔",
  urgent: "◑",
  breach: "▲",
};

export const SEVERITY_TONE: Record<NotificationSeverity, string> = {
  info: "tracking",
  warning: "approaching",
  urgent: "critical",
  breach: "breached",
};

/** Worst first, for sorting and for the badge. */
export const SEVERITY_ORDER: Record<NotificationSeverity, number> = {
  breach: 0,
  urgent: 1,
  warning: 2,
  info: 3,
};

/**
 * What kind of thing raised this.
 *
 * Written out rather than humanising the slug, because "Deadline breached" and
 * "Document bounced" mean specific things: the first is a certification
 * obligation gone past, the second is evidence that the customer received their
 * paperwork failing to exist.
 */
export const NOTIFICATION_KIND_LABEL: Record<NotificationKind, string> = {
  "deadline-approaching": "Compliance deadline",
  "deadline-breached": "Compliance deadline missed",
  "deadline-resolved": "Deadline closed late",
  "task-urgent": "Urgent task",
  "task-overdue": "Overdue task",
  "signature-completed": "Signature captured",
  "signature-declined": "Signature declined",
  "document-sent": "Document sent",
  "document-bounced": "Delivery failed",
  "chat-message": "Message",
  "plan-comment": "Note on the plan",
};

/**
 * Why a notification is no longer live, in the hub's own terms.
 *
 * Resolved and superseded are different facts and a feed that showed them
 * alike would lose the one an assessor asks about: "we warned at 14 days, again
 * at 3, and it still breached" is evidence, and it only reads that way if the
 * earlier rungs say they were replaced rather than dealt with.
 */
export function closedBecause(notification: {
  readonly live: boolean;
  readonly resolvedAt: string | null;
  readonly supersededBy: string | null;
}): string | null {
  if (notification.live) return null;
  if (notification.supersededBy !== null) {
    return "Replaced by a later, more urgent notice on the same deadline.";
  }
  if (notification.resolvedAt !== null) {
    return "Closed: the thing it was about has been dealt with.";
  }
  // Not reachable from the API's own definition of `live`, and said out loud
  // rather than defaulted, because a silent fallback here would hide a wire
  // shape that had changed underneath.
  return "No longer live; the hub did not say why.";
}

// ------------------------------------------------------------------- chat

/**
 * What to call a thread in a list.
 *
 * A job thread has a subject because the API insists on one — an unnamed thread
 * per job is the wall of history the feature exists to replace. A direct thread
 * is named after the other person, which means resolving a uuid against the
 * colleague list; where that fails the id is shown rather than a cheerful
 * "Unknown", because a name nobody can account for is worth noticing.
 */
export function threadTitle(
  thread: ChatThread,
  me: string | null,
  people: ReadonlyMap<string, Colleague>,
): string {
  if (thread.kind === "job") {
    return thread.subject ?? "Untitled thread";
  }
  const other = otherParticipant(thread, me);
  if (other === null) return "Direct message";
  return people.get(other)?.fullName ?? other;
}

/** The other end of a direct thread, or null if this is not one. */
export function otherParticipant(
  thread: ChatThread,
  me: string | null,
): string | null {
  if (thread.kind !== "direct") return null;
  if (thread.directUserAId !== null && thread.directUserAId !== me) {
    return thread.directUserAId;
  }
  if (thread.directUserBId !== null && thread.directUserBId !== me) {
    return thread.directUserBId;
  }
  return null;
}

/**
 * Who wrote a message.
 *
 * A message whose author is no longer an active member still carries their
 * name, flagged, rather than becoming an anonymous uuid — which is why the
 * colleague list includes inactive memberships. `null` is the author column's
 * own null: a user row that has been removed entirely.
 */
export function authorName(
  userId: string | null,
  me: string | null,
  people: ReadonlyMap<string, Colleague>,
): string {
  if (userId === null) return "A removed account";
  if (userId === me) return "You";
  const person = people.get(userId);
  if (person === undefined) return userId;
  return person.active ? person.fullName : `${person.fullName} (left)`;
}

/** Bytes, for an attachment. Decimal, because that is what a file manager says. */
export function fileSize(bytes: number): string {
  if (bytes < 1000) return `${bytes} bytes`;
  if (bytes < 1_000_000) return `${(bytes / 1000).toFixed(0)} kB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/**
 * A timestamp as a time of day, for a conversation read in one sitting.
 *
 * Delegated to `format.ts` rather than sliced here: a conversation timestamped
 * in the reader's local zone, beside a document timestamped in UTC, is exactly
 * the discrepancy that starts an argument, and two copies of the rule is two
 * places for one of them to drift.
 */
export function timeOfDay(iso: string): string {
  return utcMoment(iso);
}
